import React, { type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { components } from 'react-select';
import { customStyles, GenericSelect } from '@bbkAdminComponents/select/select';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import { appPersistSlice } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import {
  DEFAULT_BRAND_ID,
  useGetBrandOptions,
  useGetSelectedBrand,
} from '@bbkAdminRedux/rtkq/brands.slice';
import { cs, type Argument } from '@bbkAdminUtils/css-utils';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';

const PrefixedControl =
  (prefix: string): ReactFCC<React.ComponentProps<typeof components.Control>> =>
  ({ children, ...restProps }) => (
    <components.Control {...restProps}>
      <span className="tw-ml-2 tw-text-black/50">{prefix}</span> {children}
    </components.Control>
  );

export const BrandControl = PrefixedControl('View:');

export const BrandDisplayName: ReactFCC<{
  label: string | ReactNode;
  targeting_enabled: boolean | undefined;
  is_default: boolean;
}> = ({ label, targeting_enabled, is_default }) => (
  <div className="tw-flex">
    <span className="tw-mr-2">{label}</span>
    {!is_default && targeting_enabled !== undefined && (
      <span
        className={targeting_enabled ? 'tw-text-toggleGreen' : 'tw-text-danger'}
      >
        {targeting_enabled ? 'ACTIVE' : 'INACTIVE'}
      </span>
    )}
  </div>
);

export const BrandDropDown: ReactFCC<{
  brandId?: string;
  onChangeConfirmation?: (value: string) => void;
  onChange?: (value: string) => void;
  className?: Argument;
}> = ({ brandId, onChange, onChangeConfirmation, className }) => {
  const appKey = useSelector(selectAppId);
  const brandOptions = useGetBrandOptions({ appKey });
  const dispatch = useAppDispatch();
  const fetchedSelectedBrandId = useGetSelectedBrand(appKey);
  const selectedBrandId = brandId ?? fetchedSelectedBrandId;

  const val = brandOptions.find((b) => b.value === selectedBrandId);
  return (
    <GenericSelect
      short
      isSearchable
      components={{ Control: BrandControl }}
      options={brandOptions}
      value={val}
      styles={{
        ...customStyles,
        input: (provided) => ({
          ...provided,
          padding: '0px',
        }),
      }}
      className={cs('tw-z-20 tw-min-w-48', className)}
      onChange={({ value }) => {
        if (onChangeConfirmation) {
          onChangeConfirmation(value);
          return;
        }
        dispatch(
          appPersistSlice.actions.saveSelectedBrand({
            id: value,
            appKey,
          })
        );
        onChange?.(value);
      }}
      formatOptionLabel={(option) => (
        <BrandDisplayName
          label={option.label}
          is_default={option.value === DEFAULT_BRAND_ID}
          targeting_enabled={option.data?.targeting_enabled}
        />
      )}
    />
  );
};

export const ActiveBrandDropDown: ReactFCC<{
  brandId?: string;
  onChange?: (value: string) => void;
}> = ({ brandId, onChange }) => {
  const appKey = useSelector(selectAppId);
  const brandOptions = useGetBrandOptions({ appKey });

  const filteredBrandOptions = brandOptions.filter(
    (option) => option.value !== DEFAULT_BRAND_ID
  );
  const val = filteredBrandOptions.find((b) => b.value === brandId);

  return (
    <GenericSelect
      short
      placeholder="Choose a brand"
      maxMenuHeight={125}
      styles={{
        ...customStyles,
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
      menuPortalTarget={document.body}
      isSearchable
      components={{ Control: BrandControl }}
      options={filteredBrandOptions}
      value={val}
      className="tw-z-20 tw-min-w-64"
      onChange={({ value }) => onChange?.(value)}
      formatOptionLabel={(option) => (
        <BrandDisplayName
          label={option.label}
          is_default={option.value === DEFAULT_BRAND_ID}
          targeting_enabled={option.data?.targeting_enabled}
        />
      )}
    />
  );
};
